import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import SpecializaceServiceApi from "./SpecializaceService";
import type { Specializace } from "../generated/models/Specializace";
import type { SpecializaceRequest } from "../generated/models/SpecializaceRequest";
import type { BulkAssignSpecializaceRequest } from "../generated/models/BulkAssignSpecializaceRequest";

// -----------------------------
// SPECIALIZACE QUERIES
// -----------------------------

export const useAllSpecializaceQuery = () => {
    return useQuery<Specializace[], Error>({
        queryKey: ["specializace"],
        queryFn: () => SpecializaceServiceApi.getAllSpecializace(),
        staleTime: 5 * 60 * 1000,
    });
};

// -----------------------------
// SPECIALIZACE MUTATIONS
// -----------------------------

export const useCreateSpecializaceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Specializace, Error, SpecializaceRequest>({
        mutationFn: (data) => SpecializaceServiceApi.createSpecializace(data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["specializace"] });
            showToast("Specializace byla úspěšně vytvořena.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit specializaci.", "error");
        },
    });
};

export const useUpdateSpecializaceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Specializace, Error, { id: number; data: SpecializaceRequest }>({
        mutationFn: ({ id, data }) => SpecializaceServiceApi.updateSpecializace(id, data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["specializace"] });
            showToast("Specializace byla úspěšně aktualizována.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat specializaci.", "error");
        },
    });
};

export const useDeleteSpecializaceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => SpecializaceServiceApi.deleteSpecializace(id),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["specializace"] });
            showToast("Specializace byla úspěšně smazána.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat specializaci.", "error");
        },
    });
};

export const useBulkAssignSpecializaceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ message: string }, Error, BulkAssignSpecializaceRequest>({
        mutationFn: (data) => SpecializaceServiceApi.bulkAssignSpecializace(data),

        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            showToast(result.message || "Specializace byla přiřazena.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se přiřadit specializaci.", "error");
        },
    });
};
